import { useEffect, useRef, useState } from "react";
import api, { formatApiError, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Disclaimer, Spinner, EmptyState, UpgradeGate } from "@/components/shared";
import { DOC_CATEGORIES, labelFor } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Upload, FolderClosed, FileText, Download, Trash2, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Documents() {
  const { isPaid } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryBusy, setSummaryBusy] = useState(null);

  const load = async () => { const { data } = await api.get("/documents"); setDocs(data); setLoading(false); };
  useEffect(() => { if (isPaid) load(); else setLoading(false); }, [isPaid]);

  if (!isPaid) return (<div><PageHeader eyebrow="Document vault" title="Documents" /><UpgradeGate title="Document Vault is included in Family Path" body="Upgrade to store and organize assessments, receipts, and applications." /></div>);
  if (loading) return <Spinner label="Loading documents…" />;

  const download = (d) => window.open(`${API}/documents/${d.id}/download?auth=${getToken()}`, "_blank");

  const viewSummary = async (d) => {
    setSummaryBusy(d.id);
    try {
      let s;
      if (d.summary_status === "completed") { s = (await api.get(`/documents/${d.id}/summary`)).data; }
      else { s = (await api.post(`/documents/${d.id}/summary`)).data; load(); }
      setSummary(s);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSummaryBusy(null); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/documents/${deleteDoc.id}`); toast.success("Document deleted."); setDeleteDoc(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Document vault" title="Documents" subtitle="One calm binder for assessments, receipts, deadlines and applications."
        action={<button onClick={() => setUploadOpen(true)} className="kid-btn kid-btn-primary h-11 px-5" data-testid="documents-upload-open"><Upload size={16} /> Upload document</button>} />

      {docs.length === 0 ? (
        <EmptyState icon={FolderClosed} testid="documents-empty" title="No documents uploaded yet"
          body="Upload assessments, school documents, receipts, funding documents, or notes to keep them organized."
          action={<button onClick={() => setUploadOpen(true)} className="kid-btn kid-btn-primary h-12 px-7"><Upload size={16} /> Upload document</button>} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 kid-stagger">
          {docs.map((d) => (
            <div key={d.id} className="kid-card p-5" data-testid="document-card">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-kid-skytint text-kid-teal grid place-items-center shrink-0"><FileText size={20} /></div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-kid-navy truncate">{d.display_name}</h4>
                  <p className="text-xs text-kid-muted mt-0.5">{labelFor(DOC_CATEGORIES, d.category)} · {(d.file_size_bytes / 1024).toFixed(0)} KB</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => download(d)} className="text-sm text-kid-teal font-poppins font-semibold inline-flex items-center gap-1" data-testid="document-download"><Download size={14} /> Download</button>
                <button onClick={() => viewSummary(d)} disabled={summaryBusy === d.id} className="text-sm text-kid-grape font-poppins font-semibold inline-flex items-center gap-1" data-testid="document-summary">
                  <Sparkles size={14} /> {summaryBusy === d.id ? "Summarizing…" : d.summary_status === "completed" ? "View AI summary" : "AI summary"}
                </button>
                <button onClick={() => setDeleteDoc(d)} className="text-sm text-kid-coral font-poppins font-semibold inline-flex items-center gap-1 ml-auto" data-testid="document-delete"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6"><Disclaimer>Your documents are private and controlled by you. You can delete them at any time.</Disclaimer></div>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onDone={() => { setUploadOpen(false); load(); }} />

      <Dialog open={!!summary} onOpenChange={() => setSummary(null)}>
        <DialogContent data-testid="summary-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-kid-grape" /> AI Summary</DialogTitle></DialogHeader>
          <div className="text-sm text-kid-ink whitespace-pre-wrap max-h-[50vh] overflow-y-auto">{summary?.summary_text}</div>
          <p className="text-xs text-kid-muted border-t border-kid-line pt-3">{summary?.disclaimer}</p>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteDoc} onOpenChange={() => setDeleteDoc(null)}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from your Document Vault. Any linked tasks remain, but the document link is removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-kid-coral hover:bg-kid-coral/90" data-testid="delete-doc-confirm">Delete document</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UploadModal({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("other");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const submit = async () => {
    setError("");
    if (!file) { setError("Please choose a file."); return; }
    if (!consent) { setError("Please provide consent to store this document."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("consent", "true");
    fd.append("display_name", file.name);
    setBusy(true);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded."); setFile(null); setConsent(false); onDone(); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="upload-dialog">
        <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {error && <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</div>}
          <button onClick={() => inputRef.current?.click()} className="w-full border-2 border-dashed border-kid-line rounded-2xl py-8 text-center hover:border-kid-teal transition-colors" data-testid="upload-dropzone">
            <Upload className="mx-auto text-kid-teal mb-2" />
            <p className="text-sm text-kid-navy font-semibold">{file ? file.name : "Choose a file"}</p>
            <p className="text-xs text-kid-muted mt-1">PDF, JPG, PNG, DOC, DOCX · up to 15 MB</p>
          </button>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" data-testid="upload-file-input"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-3 text-sm text-kid-muted cursor-pointer">
            <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="upload-consent" className="mt-0.5" />
            <span>I understand this document may contain sensitive information and consent to storing it in my KidPath account.</span>
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="kid-btn kid-btn-ghost h-11 px-5">Cancel</button>
          <button onClick={submit} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5" data-testid="upload-submit">{busy ? "Uploading…" : "Upload document"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
